import React, { useEffect, useState } from "react";
import { fetchCustomFields, CustomField, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/providers/AuthProvider";
import { useTranslation } from "react-i18next";

interface ListCustomFieldsEditTabProps {
  list: any;
  onChange: (customFields: Record<string, any>) => void;
  isEditing: boolean;
}

export function ListCustomFieldsEditTab({ list, onChange, isEditing }: ListCustomFieldsEditTabProps) {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.id;
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    fetchCustomFields(organizationId)
      .then((data: CustomFieldsJSON) => {
        const defs = data.Lists || [];
        setFields(defs);
      })
      .catch(() => {
        setError("Failed to load custom fields");
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const currentCustomFields = list.custom_fields || {};
    const updatedCustomFields = {
      ...currentCustomFields,
      [fieldName]: value,
    };
    onChange(updatedCustomFields);
  };

  if (loading) {
    return <div className="space-y-4">
      <div>{t("lists.customFields.loading", "Loading custom fields...")}</div>
    </div>;
  }

  if (error) {
    return <div className="space-y-4">
      <div className="text-red-600">{error}</div>
    </div>;
  }

  if (fields.length === 0) {
    return <div className="space-y-4">
      <div className="text-gray-500">{t("lists.customFields.noFields", "No custom fields configured for lists.")}</div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{t("lists.customFields.title", "Custom Fields")}</h3>
        <p className="text-gray-500 text-sm">{t("lists.customFields.description", "Manage custom fields for this list.")}</p>
      </div>
      
      <div className="space-y-4">
        {fields.map((field) => {
          const currentValue = list.custom_fields && list.custom_fields[field.name] !== undefined
            ? list.custom_fields[field.name]
            : "";
          
          return (
            <div key={field.name} className="flex flex-col gap-2">
              <label className="font-medium text-sm">{field.name}</label>
              
              {field.type === "text" && (
                <Input
                  value={currentValue}
                  onChange={e => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.name}
                  disabled={!isEditing}
                  aria-label={field.name}
                />
              )}
              
              {field.type === "number" && (
                <Input
                  type="number"
                  value={currentValue}
                  onChange={e => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.name}
                  disabled={!isEditing}
                  aria-label={field.name}
                />
              )}
              
              {field.type === "boolean" && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!currentValue}
                    onCheckedChange={checked => isEditing && handleFieldChange(field.name, checked)}
                    id={field.name}
                    disabled={!isEditing}
                    aria-label={field.name}
                  />
                  <label htmlFor={field.name} className="text-sm">{field.name}</label>
                </div>
              )}
              
              {field.type === "dropdown" && field.options && (
                <Select
                  value={currentValue}
                  onValueChange={val => handleFieldChange(field.name, val)}
                  disabled={!isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={field.name} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
